import React from 'react';
import { Input, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import ColumnRow from './ColumnRow';
import { useColumnPreferences } from './useColumnPreferences';
import { IArrangeColumnsModalProps } from './columnPreferences.types';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import { Checkbox } from '../../components/Checkbox';
import { GAEventIdentifier, GAEventName } from '../../section/Common/constants';
import './styles.css';

const ArrangeColumnsModal: React.FC<IArrangeColumnsModalProps> = ({
  visible,
  tableId,
  allColumns,
  lockedColumns = [],
  roleDefaultPreferences = null,
  onCancel,
  onSave
}) => {
  const {
    search,
    setSearch,
    filteredOrder,
    hidden,
    showAllState,
    dragIndex,
    toggleColumn,
    toggleShowAll,
    handleDragStart,
    handleDragEnter,
    handleDragEnd,
    resetToDefault,
    commit,
    isSaving
  } = useColumnPreferences({
    tableId,
    allColumns,
    lockedColumns,
    isOpen: visible,
    roleDefaultPreferences
  });

  if (!visible) {
    return null; }

  const handleSave = async () => {
    window.gtag('event', GAEventName.click, {
      event_category: 'Interaction',
      identifier: GAEventIdentifier.saveColumns
    });

    try {
      const result = await commit();

      message.success({
        content: 'Column preferences saved.',
        duration: 5
      });

      onSave(result);
    } catch (error) {
      console.error(error);

      message.error({
        content: 'Unable to save preferences. Please try again.',
        duration: 5
      });
    }
  };

  return (
    <Modal
      title='Arrange Columns'
      visible={visible}
      onCancel={onCancel}
      footer={null}
      className='arrange-columns-modal'
    >
      <p className='arrange-columns-subtitle'>
        Select which columns you want to see and drag to rearrange the order
      </p>

      <Input
        className='arrange-columns-search'
        placeholder='Search column names'
        prefix={<SearchOutlined />}
        value={search}
        onChange={event => setSearch(event.target.value)}
        allowClear
      />

      <div className='arrange-columns-show-all'>
        <Checkbox
          checked={showAllState === 'all'}
          indeterminate={showAllState === 'partial'}
          onChange={toggleShowAll}
        >
          Show all
        </Checkbox>
      </div>

      <ul className='arrange-columns-list'>
        {filteredOrder.map((columnName, index) => (
          <ColumnRow
            key={columnName}
            columnName={columnName}
            index={index}
            isChecked={!hidden.includes(columnName)}
            isDragging={dragIndex === index}
            onToggle={toggleColumn}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragEnd={handleDragEnd}
          />
        ))}
        {filteredOrder.length === 0 && (
          <li className='arrange-columns-empty'>No columns match your search.</li>
        )}
      </ul>

      <div className='arrange-columns-footer'>
        <button
          type='button'
          className='arrange-columns-reset'
          onClick={() => {
            window.gtag('event', GAEventName.click, {
              event_category: 'Interaction',
              identifier: GAEventIdentifier.resetColumns
            });
            resetToDefault();
          }}
        >
          Reset to default
        </button>
        <div className='arrange-columns-footer-actions'>
          <Button className='arrange-columns-cancel' onClick={onCancel}>
            Cancel
          </Button>
          <Button className='arrange-columns-save' onClick={handleSave} loading={isSaving}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ArrangeColumnsModal;